import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { Select } from '../../components/form/Select';
import { PageLoader } from '../../components/ui/PageLoader/PageLoader';
import { useToast } from '../../hooks/useToast';
import { useCustomerDetail } from './hooks/useCustomerDetail';
import { useUpdateCustomer } from './hooks/useCustomerMutations';
import { useAuthStore } from '../../store/authStore';
import type { AccountStatus, CustomerType, UpdateCustomerInput } from '../../types/api';
import styles from './CustomerEditPage.module.css';

const CUSTOMER_TYPE_OPTIONS = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
];

const ACCOUNT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

interface FormState {
  customer_type: CustomerType;
  name: string;
  phone: string;
  email: string;
  phone2: string;
  address: string;
  postcode: string;
  company_name: string;
  vat_number: string;
  payment_terms: string;
  credit_limit: string;
  account_name: string;
  account_number: string;
  sort_code: string;
  account_status: AccountStatus;
}

const EMPTY: FormState = {
  customer_type: 'residential',
  name: '',
  phone: '',
  email: '',
  phone2: '',
  address: '',
  postcode: '',
  company_name: '',
  vat_number: '',
  payment_terms: '',
  credit_limit: '',
  account_name: '',
  account_number: '',
  sort_code: '',
  account_status: 'active',
};

export function CustomerEditPage(): JSX.Element {
  const { id: uuid } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  const detail = useCustomerDetail(uuid ?? '');
  const updateMutation = useUpdateCustomer(uuid ?? '');

  const [form, setForm] = useState<FormState>(EMPTY);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!detail.data || hydrated) return;
    const c = detail.data;
    setForm({
      customer_type: c.customer_type,
      name: c.full_name ?? c.name ?? '',
      phone: c.phone ?? '',
      email: c.email ?? '',
      phone2: c.phone2 ?? '',
      address: c.address ?? '',
      postcode: c.postcode ?? '',
      company_name: c.company_name ?? '',
      vat_number: c.vat_number ?? '',
      payment_terms: c.payment_terms ?? '',
      credit_limit: c.credit_limit !== null && c.credit_limit !== undefined ? String(c.credit_limit) : '',
      account_name: c.account_name ?? '',
      account_number: c.account_number ?? '',
      sort_code: c.sort_code ?? '',
      account_status: c.account_status,
    });
    setHydrated(true);
  }, [detail.data, hydrated]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const validationErrors = useMemo(() => {
    const issues: string[] = [];
    if (!form.name.trim()) issues.push('Name is required.');
    if (!form.address.trim()) issues.push('Address is required.');
    if (!form.postcode.trim()) issues.push('Postcode is required.');
    return issues;
  }, [form]);

  const canSubmit = validationErrors.length === 0 && !updateMutation.isPending && hydrated;

  async function handleSubmit() {
    if (!canSubmit) return;

    const input: UpdateCustomerInput = {
      name: form.name.trim(),
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      phone2: form.phone2.trim() || null,
      address: form.address.trim(),
      postcode: form.postcode.trim().toUpperCase(),
      customer_type: form.customer_type,
      account_status: form.account_status,
    };

    if (form.customer_type === 'commercial') {
      input.company_name = form.company_name.trim() || null;
      input.vat_number = form.vat_number.trim() || null;
      input.payment_terms = form.payment_terms.trim() || null;
      input.credit_limit = form.credit_limit ? Number(form.credit_limit) : null;
    }

    if (isAdmin) {
      input.account_name = form.account_name.trim() || null;
      input.account_number = form.account_number.trim() || null;
      input.sort_code = form.sort_code.trim() || null;
    }

    try {
      await updateMutation.mutateAsync(input);
      toast.success('Customer updated');
      navigate(`/customers/${encodeURIComponent(uuid ?? '')}`);
    } catch {
      // toast handled in hook
    }
  }

  if (!uuid) return <PageLoader message="Loading…" />;
  if (detail.isLoading) return <PageLoader message="Loading customer…" />;
  if (detail.isError || !detail.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Customer not found" />
        <Card>
          <p>Could not load customer.</p>
          <Button variant="secondary" onClick={() => navigate('/customers')}>
            Back to customers
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Edit ${detail.data.full_name}`}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate(`/customers/${encodeURIComponent(uuid)}`)}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
        }
      />

      <Card title="Customer details">
        <div className={styles.grid2}>
          <Select
            label="Customer type"
            options={CUSTOMER_TYPE_OPTIONS}
            value={form.customer_type}
            onChange={(e) => setField('customer_type', e.target.value as CustomerType)}
          />
          <div />
          <Input
            label="Name"
            required
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
          />
          <Input
            label="Phone"
            variant="tel"
            value={form.phone}
            onChange={(e) => setField('phone', e.target.value)}
          />
          <Input
            label="Email"
            variant="email"
            value={form.email}
            onChange={(e) => setField('email', e.target.value)}
          />
          <Input
            label="Phone (alt)"
            variant="tel"
            value={form.phone2}
            onChange={(e) => setField('phone2', e.target.value)}
          />
          <Input
            label="Address"
            required
            value={form.address}
            onChange={(e) => setField('address', e.target.value)}
          />
          <Input
            label="Postcode"
            required
            value={form.postcode}
            onChange={(e) => setField('postcode', e.target.value.toUpperCase())}
          />
        </div>
      </Card>

      {form.customer_type === 'commercial' ? (
        <Card title="Business details">
          <div className={styles.grid2}>
            <Input
              label="Company name"
              value={form.company_name}
              onChange={(e) => setField('company_name', e.target.value)}
            />
            <Input
              label="VAT number"
              value={form.vat_number}
              onChange={(e) => setField('vat_number', e.target.value)}
            />
            <Input
              label="Payment terms"
              value={form.payment_terms}
              onChange={(e) => setField('payment_terms', e.target.value)}
            />
            <Input
              label="Credit limit (£)"
              variant="number"
              value={form.credit_limit}
              onChange={(e) => setField('credit_limit', e.target.value)}
            />
          </div>
        </Card>
      ) : null}

      {isAdmin ? (
        <Card title="Banking details">
          <div className={styles.grid2}>
            <Input
              label="Account name"
              value={form.account_name}
              onChange={(e) => setField('account_name', e.target.value)}
            />
            <Input
              label="Account number"
              value={form.account_number}
              onChange={(e) => setField('account_number', e.target.value)}
            />
            <Input
              label="Sort code (NN-NN-NN)"
              value={form.sort_code}
              onChange={(e) => setField('sort_code', e.target.value)}
            />
            <Select
              label="Account status"
              options={ACCOUNT_STATUS_OPTIONS}
              value={form.account_status}
              onChange={(e) => setField('account_status', e.target.value as AccountStatus)}
            />
          </div>
        </Card>
      ) : null}

      <div className={styles.submitBar}>
        <ul className={styles.errorList} aria-live="polite">
          {validationErrors.slice(0, 4).map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
        </ul>
        <div className={styles.submitActions}>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            loading={updateMutation.isPending}
            disabled={!canSubmit}
          >
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}
